/* Holiday landing page sections (holiday-*): hero slideshow and discount code, sticky in-page nav, add to cart, gift details, gift finder, FAQ and back to top. */
(function () {
  'use strict';

  if (window.holidayLandingLoaded) return;
  window.holidayLandingLoaded = true;

  var root = document.documentElement;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* ---------- Sticky offsets ----------
     The theme header sticks to the top and shrinks once the page scrolls.
     Expose its height so the in-page nav can sit right under it and anchors land below both. */
  function initStickyOffsets() {
    var headerSection = document.querySelector('.header-section');

    function update() {
      var subnav = document.querySelector('.hol-subnav');
      var top = 0;
      // The menu row is absolutely positioned inside the section's bottom padding, so measure the whole section.
      if (headerSection && getComputedStyle(headerSection).position === 'sticky') {
        top = Math.max(0, Math.round(headerSection.getBoundingClientRect().height));
      }
      root.style.setProperty('--hol-sticky-top', top + 'px');
      root.style.setProperty('--hol-subnav-height', (subnav ? subnav.offsetHeight : 0) + 'px');
    }

    update();
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    document.addEventListener('headerStickyChange', function () {
      // The header animates between sizes; measure again once it settles.
      update();
      window.setTimeout(update, 350);
    });
    if ('ResizeObserver' in window && headerSection) {
      new ResizeObserver(update).observe(headerSection);
    }
  }

  /* ---------- Hero slideshow ---------- */
  function initHero(carousel) {
    var slides = Array.prototype.slice.call(carousel.querySelectorAll('[data-hol-slide]'));
    var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-hol-dot]'));
    var pauseButton = carousel.querySelector('[data-hol-pause]');
    var caption = carousel.querySelector('[data-hol-caption]');
    var link = carousel.querySelector('[data-hol-caption-link]');
    var interval = (parseFloat(carousel.dataset.interval) || 5) * 1000;
    var current = 0;
    var paused = reducedMotion.matches;
    var hovered = false;
    var focused = false;
    var timer = null;

    if (slides.length < 2) return;

    function stop() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function updatePause() {
      if (!pauseButton) return;
      pauseButton.classList.toggle('is-paused', paused);
      pauseButton.setAttribute('aria-label', paused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel);
      pauseButton.setAttribute('title', paused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel);
    }

    function show(index) {
      current = (index + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === current);
        slide.setAttribute('aria-hidden', String(i !== current));
      });
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-pressed', String(i === current));
      });

      var slide = slides[current];
      if (caption) caption.textContent = slide.dataset.caption || '';
      if (link) {
        link.hidden = !slide.dataset.href || !slide.dataset.link;
        link.href = slide.dataset.href || '#';
        link.querySelector('span').textContent = slide.dataset.link || '';
      }
    }

    function schedule() {
      stop();
      // The theme editor can replace this section; stop the old timer once it is gone.
      if (!carousel.isConnected) return;
      if (paused || hovered || focused || document.hidden) return;
      timer = window.setTimeout(function () {
        show(current + 1);
        schedule();
      }, interval);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        show(i);
        paused = true;
        updatePause();
        schedule();
      });
    });

    if (pauseButton) {
      pauseButton.addEventListener('click', function () {
        paused = !paused;
        updatePause();
        schedule();
      });
    }

    carousel.addEventListener('mouseenter', function () {
      hovered = true;
      stop();
    });
    carousel.addEventListener('mouseleave', function () {
      hovered = false;
      schedule();
    });
    carousel.addEventListener('focusin', function () {
      focused = true;
      stop();
    });
    carousel.addEventListener('focusout', function () {
      window.setTimeout(function () {
        focused = carousel.contains(document.activeElement);
        schedule();
      }, 0);
    });
    carousel.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      show(current + (event.key === 'ArrowRight' ? 1 : -1));
      paused = true;
      updatePause();
      schedule();
    });
    document.addEventListener('visibilitychange', schedule);
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', function (event) {
        paused = event.matches;
        updatePause();
        schedule();
      });
    }

    show(0);
    updatePause();
    schedule();
  }

  /* ---------- Dialogs ---------- */
  var lastFocus = null;

  // Elements are bound once; the theme editor re-renders sections and init runs again.
  function bindOnce(el) {
    if (el.dataset.holBound) return false;
    el.dataset.holBound = 'true';
    return true;
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    lastFocus = document.activeElement;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    root.classList.add('hol-modal-open');
  }

  function closeDialog(dialog) {
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  }

  function initDialog(dialog) {
    if (!bindOnce(dialog)) return;
    dialog.addEventListener('close', function () {
      root.classList.remove('hol-modal-open');
      if (lastFocus && lastFocus.isConnected) lastFocus.focus();
    });
    // A click on the backdrop closes the dialog.
    dialog.addEventListener('click', function (event) {
      if (event.target !== dialog) return;
      var rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        closeDialog(dialog);
      }
    });
  }

  function showGiftDetails(id) {
    var dialog = document.getElementById('hol-detail');
    var template = document.getElementById('hol-detail-' + id);
    if (!dialog || !template) return;
    var content = dialog.querySelector('[data-hol-detail-content]');
    content.innerHTML = '';
    content.appendChild(template.content.cloneNode(true));
    enhanceSelects(content);
    openDialog(dialog);
  }

  /* ---------- Gift finder ----------
     The finder asks either interest and budget (gift sets) or category and format (restock: products from every section).
     Two forms share this code: the finder dialog in the Gift sets section and the inline Gift finder
     section. Each sits in a [data-hol-finder-root] holding its messages and results. */
  function inBudget(price, budget) {
    if (budget === 'any') return true;
    // "min-max" in cents (an empty max has no limit); a plain number means "under".
    if (budget.indexOf('-') === -1) return price < Number(budget);
    var parts = budget.split('-');
    return price >= Number(parts[0]) && (parts[1] === '' || price < Number(parts[1]));
  }

  function findGifts(products, interest, budget) {
    var eligible = products.filter(function (p) {
      return inBudget(p.price, budget);
    });
    var matched = eligible.filter(function (p) {
      return interest === 'any' || p.kind === interest;
    });
    return { exact: matched.length > 0, products: matched.length ? matched : eligible };
  }

  // Restock finder: products in the category, narrowed to sets or singles when the category has them.
  function findRestock(products, need, format) {
    var relevant = products.filter(function (p) {
      return need === 'any' || (p.needs || []).indexOf(need) !== -1;
    });
    var matched = relevant.filter(function (p) {
      return format === 'any' || p.format === format;
    });
    return { exact: matched.length > 0, products: matched.length ? matched : relevant };
  }

  // Every product the restock finder can suggest: the gift sets plus the other sections' lists, once each.
  function restockProducts() {
    var lists = [document.getElementById('hol-gift-data')].concat(Array.prototype.slice.call(document.querySelectorAll('[data-hol-finder-items]')));
    var seen = {};
    var products = [];
    lists.forEach(function (el) {
      if (!el) return;
      JSON.parse(el.textContent).forEach(function (p) {
        if (!p.needs || !p.needs.length || seen[p.url]) return;
        seen[p.url] = true;
        products.push(p);
      });
    });
    return products;
  }

  function finderAction(p, messages, arrow, bag) {
    // Sets with a choice (e.g. hair type) open their selection step, or link to the product page.
    if (p.options && document.getElementById('hol-options-' + p.options)) {
      return '<button class="' + escapeHtml(messages.buttonClass || 'hol__btn-outline') + ' hol-finder__add" type="button" data-hol-options="' + escapeHtml(p.options) + '">' + escapeHtml(messages.chooseLabel || messages.exploreLabel) + '</button>';
    }
    if (!p.variant) {
      return '<a href="' + escapeHtml(p.url) + '">' + escapeHtml(messages.exploreLabel) + ' ' + arrow + '</a>';
    }
    return (
      '<form action="' + escapeHtml(messages.cartAddUrl) + '" method="post" data-hol-buy data-added-label="' + escapeHtml(messages.addedLabel) + '">' +
      '<input type="hidden" name="id" value="' + escapeHtml(p.variant) + '"><input type="hidden" name="quantity" value="1">' +
      '<button class="' + escapeHtml(messages.buttonClass || 'hol__btn-outline') + ' hol-finder__add" type="submit" data-hol-buy-button' + (p.available ? '' : ' disabled') + '>' +
      '<span data-hol-buy-text>' + escapeHtml(p.available ? messages.addLabel : messages.soldOutLabel) + '</span> ' + bag + '</button>' +
      '<p class="hol-buy__error" role="alert" data-hol-buy-error hidden></p>' +
      '</form>'
    );
  }

  function showFinderResults(root, form) {
    var dataEl = document.getElementById('hol-gift-data');
    var messages = root.dataset;
    var arrow = root.querySelector('[data-hol-arrow]').innerHTML;
    var bag = root.querySelector('[data-hol-bag]').innerHTML;
    var results = root.querySelector('[data-hol-finder-results]');
    var result;
    if (form.elements.need) {
      result = findRestock(restockProducts(), form.elements.need.value, form.elements.format.value);
    } else {
      result = findGifts(dataEl ? JSON.parse(dataEl.textContent) : [], form.elements.interest.value, form.elements.budget.value);
    }
    var text = result.exact ? messages.messageMatch : messages.messageFallback;
    if (!result.products.length) text = messages.messageNone;

    var html = '<p class="hol-finder__message">' + escapeHtml(text) + '</p>';
    html += result.products
      .map(function (p) {
        return (
          '<div class="hol-finder__result">' +
          (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="" loading="lazy">' : '<span></span>') +
          '<div><h3><a href="' + escapeHtml(p.url) + '" aria-label="' + escapeHtml(messages.exploreLabel + ' ' + p.name) + '">' + escapeHtml(p.name) + '</a></h3><p>' + escapeHtml(p.price_label) + (p.count ? ' · ' + escapeHtml(p.count) : '') + '</p></div>' +
          finderAction(p, messages, arrow, bag) +
          '</div>'
        );
      })
      .join('');
    var giftcard = root.querySelector('[data-hol-finder-giftcard]');
    if (giftcard) html += giftcard.innerHTML;

    results.innerHTML = html;
    results.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  function initFinder() {
    var dialog = document.getElementById('hol-finder');
    var inline = document.querySelector('[data-hol-finder-inline]');

    // Finder links elsewhere on the page only show when a finder is on the page.
    document.querySelectorAll('[data-hol-finder], [data-hol-finder-wrap]').forEach(function (el) {
      el.hidden = !dialog && !inline;
    });
    if (dialog) initDialog(dialog);

    document.querySelectorAll('[data-hol-finder-form]').forEach(function (form) {
      // Restock finder: hide categories that no product on the page covers.
      if (form.elements.need) {
        var products = restockProducts();
        Array.prototype.forEach.call(form.elements.need.options, function (option) {
          if (option.value === 'any') return;
          option.hidden = option.disabled = !products.some(function (p) {
            return p.needs.indexOf(option.value) !== -1;
          });
        });
      }
      if (!bindOnce(form)) return;
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        showFinderResults(form.closest('[data-hol-finder-root]'), form);
      });
    });
  }

  function openFinder() {
    var dialog = document.getElementById('hol-finder');
    if (dialog) {
      openDialog(dialog);
      return;
    }
    var inline = document.querySelector('[data-hol-finder-inline]');
    if (!inline) return;
    inline.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    var first = inline.querySelector('select');
    if (first) first.focus({ preventScroll: true });
  }

  /* ---------- Selection step (compact gift set cards) ---------- */
  function showOptions(id) {
    var dialog = document.getElementById('hol-detail');
    var template = document.getElementById('hol-options-' + id);
    if (!dialog || !template) return false;
    // Opened from the gift finder: swap the finder for the selection step.
    var finder = document.getElementById('hol-finder');
    if (finder && finder.hasAttribute('open')) closeDialog(finder);
    var content = dialog.querySelector('[data-hol-detail-content]');
    content.innerHTML = '';
    content.appendChild(template.content.cloneNode(true));
    enhanceSelects(content);
    openDialog(dialog);
    return true;
  }

  /* ---------- Add to cart ----------
     Forms post to /cart/add without JavaScript. Here they add in place, then the theme's
     ajaxProduct:added listener refreshes the cart and opens it. */
  function setBuyText(form, html) {
    var text = form.querySelector('[data-hol-buy-text]');
    if (text) text.innerHTML = html;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-hol-buy]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.busy) return;
    if (form.hasAttribute('data-require-choice') && !form.elements.id.value) {
      var wrap = form.querySelector('.hol-select');
      if (wrap && wrap.__open) wrap.__open();
      return;
    }

    var button = form.querySelector('[data-hol-buy-button]');
    var error = form.querySelector('[data-hol-buy-error]');
    var text = form.querySelector('[data-hol-buy-text]');
    var original = text ? text.innerHTML : '';
    form.dataset.busy = 'true';
    if (button) button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    fetch(form.getAttribute('action') + '.js', {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        // Modal dialogs sit above the cart; close them so it shows.
        var dialog = form.closest('dialog');
        if (dialog) closeDialog(dialog);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
        if (form.dataset.addedLabel) {
          setBuyText(form, escapeHtml(form.dataset.addedLabel));
          window.setTimeout(function () {
            if (form.isConnected) setBuyText(form, original);
          }, 2500);
        }
      })
      .catch(function (data) {
        if (!error) return;
        error.textContent = (data && (data.description || data.message)) || 'Something went wrong. Please try again.';
        error.hidden = false;
      })
      .then(function () {
        delete form.dataset.busy;
        if (button) button.removeAttribute('aria-busy');
      });
  });

  // Choosing a flavour, size or amount updates the price shown on the card and button.
  document.addEventListener('change', function (event) {
    var form = event.target.closest('[data-hol-buy]');
    if (!form || event.target.name !== 'id') return;
    var input = event.target;
    var choice = input.tagName === 'SELECT' ? input.options[input.selectedIndex] : input;
    var price = choice && choice.dataset.price;
    if (!price) return;
    var scope = form.closest('[data-hol-buy-scope]') || form;
    scope.querySelectorAll('[data-hol-buy-price]').forEach(function (el) {
      el.textContent = price;
    });
  });

  /* ---------- Dropdowns ----------
     Same custom list as the Tanit Box (bundle-builder.js). The native select stays in the form,
     hidden, and keeps the value; the button and list mirror it. */
  function closeAllSelects(except) {
    document.querySelectorAll('.hol-select.is-open').forEach(function (wrap) {
      if (wrap !== except && wrap.__close) wrap.__close();
    });
  }

  function enhanceSelect(select) {
    if (select.__holEnhanced) return;
    select.__holEnhanced = true;

    var label = select.closest('label');
    var labelText = label && label.querySelector('.hol-buy__label');
    var wrap = document.createElement('div');
    wrap.className = 'hol-select';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hol-select__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    var list = document.createElement('ul');
    list.className = 'hol-select__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    if (labelText) {
      if (!labelText.id) labelText.id = 'hol-select-' + Math.random().toString(36).slice(2, 9);
      list.setAttribute('aria-labelledby', labelText.id);
    }

    function text(opt) {
      return '<span class="hol-select__text">' + escapeHtml(opt.textContent.trim()) + '</span>';
    }

    Array.prototype.forEach.call(select.options, function (opt) {
      // The "Choose a ..." placeholder only shows on the button.
      if (opt.value === '') return;
      var li = document.createElement('li');
      li.className = 'hol-select__option' + (opt.disabled ? ' is-disabled' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', opt.value);
      if (opt.disabled) li.setAttribute('aria-disabled', 'true');
      li.tabIndex = -1;
      li.innerHTML = text(opt) + '<span class="hol-select__check" aria-hidden="true"></span>';
      list.appendChild(li);
    });

    function render() {
      var opt = select.options[select.selectedIndex];
      btn.classList.toggle('is-placeholder', !opt || opt.value === '');
      btn.innerHTML =
        (opt ? text(opt) : '<span class="hol-select__text"></span>') +
        '<svg class="hol-select__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
      btn.setAttribute('aria-label', (labelText ? labelText.textContent.trim() + ': ' : '') + (opt ? opt.textContent.trim() : ''));
      Array.prototype.forEach.call(list.children, function (li) {
        var selected = li.getAttribute('data-value') === select.value;
        li.setAttribute('aria-selected', String(selected));
        li.classList.toggle('is-selected', selected);
      });
    }

    function open() {
      if (!list.hidden) return;
      closeAllSelects(wrap);
      list.hidden = false;
      wrap.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      // Flip upwards when there is no room below.
      var rect = list.getBoundingClientRect();
      wrap.classList.toggle('is-up', rect.bottom > window.innerHeight - 8 && rect.top > rect.height + 8);
    }

    function close() {
      if (list.hidden) return;
      list.hidden = true;
      wrap.classList.remove('is-open', 'is-up');
      btn.setAttribute('aria-expanded', 'false');
    }

    function choose(li) {
      if (!li || li.classList.contains('is-disabled')) return;
      var value = li.getAttribute('data-value');
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      render();
      close();
      btn.focus();
    }

    function focusMove(delta) {
      var items = Array.prototype.filter.call(list.children, function (li) {
        return !li.classList.contains('is-disabled');
      });
      if (!items.length) return;
      var idx = items.indexOf(document.activeElement);
      if (idx === -1) idx = items.findIndex(function (li) { return li.classList.contains('is-selected'); });
      idx = (idx + delta + items.length) % items.length;
      items[idx].focus();
    }

    btn.addEventListener('click', function (event) {
      // The dropdown sits inside a <label>; keep the click from reaching the hidden select.
      event.preventDefault();
      if (list.hidden) open();
      else close();
    });
    btn.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
        focusMove(event.key === 'ArrowDown' ? 1 : -1);
      }
    });
    list.addEventListener('click', function (event) {
      event.preventDefault();
      choose(event.target.closest('.hol-select__option'));
    });
    list.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') { event.preventDefault(); focusMove(1); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); focusMove(-1); }
      else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(document.activeElement); }
      else if (event.key === 'Escape') { event.preventDefault(); close(); btn.focus(); }
      else if (event.key === 'Tab') close();
    });
    select.addEventListener('change', render);

    wrap.__close = close;
    wrap.__open = function () {
      open();
      focusMove(1);
    };
    // The add-to-cart handler checks a choice was made, and opens this list if not.
    if (select.required && select.form) select.form.noValidate = true;
    wrap.appendChild(btn);
    wrap.appendChild(list);
    select.classList.add('hol-select__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    select.parentNode.insertBefore(wrap, select.nextSibling);
    render();
  }

  function enhanceSelects(root) {
    root.querySelectorAll('.hol-buy select').forEach(enhanceSelect);
  }

  // Gift set cards without a dropdown reserve its height so their rows line up with the others.
  function alignGiftRows(grid) {
    function update() {
      var tallest = 0;
      grid.querySelectorAll('.hol-buy__option').forEach(function (option) {
        tallest = Math.max(tallest, option.offsetHeight);
      });
      if (tallest) grid.style.setProperty('--hol-option-space', tallest + 8 + 'px');
    }
    update();
    if ('ResizeObserver' in window) {
      grid.querySelectorAll('.hol-buy__option').forEach(function (option) {
        new ResizeObserver(update).observe(option);
      });
    } else {
      window.addEventListener('resize', update);
    }
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest('.hol-select')) closeAllSelects();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllSelects();
  });

  /* ---------- Copy the discount code (hero) ---------- */
  function copyCode(button) {
    var status = button.parentNode.querySelector('[data-hol-copy-status]');
    var original = button.dataset.label || button.textContent;
    button.dataset.label = original;
    navigator.clipboard.writeText(button.dataset.holCopy).then(
      function () {
        button.textContent = button.dataset.copiedLabel || original;
        if (status) status.textContent = button.dataset.copiedStatus || '';
        window.setTimeout(function () {
          if (button.isConnected) button.textContent = original;
        }, 2500);
      },
      function () {
        // Clipboard blocked: select the code so it can be copied by hand.
        var value = button.parentNode.querySelector('.hol-hero__code-value');
        if (!value || !window.getSelection) return;
        var range = document.createRange();
        range.selectNodeContents(value);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    );
  }

  /* ---------- Back to top ---------- */
  function initBackToTop() {
    var ticking = false;
    var lastY = window.scrollY;
    var scrollingUp = false;

    // Shown only while scrolling back up, so it doesn't sit over the purchase buttons while shopping.
    function update() {
      ticking = false;
      var y = window.scrollY;
      if (Math.abs(y - lastY) > 8) {
        scrollingUp = y < lastY;
        lastY = y;
      }
      var visible = y > window.innerHeight && scrollingUp;
      document.querySelectorAll('[data-hol-top]').forEach(function (button) {
        button.classList.toggle('is-visible', visible);
      });
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ---------- Clicks (delegated so re-rendered sections keep working) ---------- */
  document.addEventListener('click', function (event) {
    var target = event.target.closest('[data-hol-quick], [data-hol-options], [data-hol-finder], [data-hol-close], [data-hol-faq-toggle], [data-hol-top], [data-hol-copy]');
    if (!target) return;

    if (target.hasAttribute('data-hol-copy')) {
      copyCode(target);
    } else if (target.hasAttribute('data-hol-top')) {
      window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      // Send keyboard focus back to the start of the page too.
      var main = document.getElementById('MainContent');
      if (main) {
        if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    } else if (target.hasAttribute('data-hol-quick')) {
      showGiftDetails(target.dataset.holQuick);
    } else if (target.hasAttribute('data-hol-options')) {
      if (!showOptions(target.dataset.holOptions) && target.dataset.href) window.location.href = target.dataset.href;
    } else if (target.hasAttribute('data-hol-finder')) {
      openFinder();
    } else if (target.hasAttribute('data-hol-close')) {
      closeDialog(target.closest('dialog'));
    } else {
      var panel = document.getElementById(target.getAttribute('aria-controls'));
      var open = target.getAttribute('aria-expanded') !== 'true';
      target.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
    }
  });

  function init() {
    document.querySelectorAll('[data-hol-carousel]').forEach(function (carousel) {
      if (bindOnce(carousel)) initHero(carousel);
    });
    // FAQ answers are rendered open so they work without JavaScript; collapse them here.
    document.querySelectorAll('[data-hol-faq-toggle]').forEach(function (button) {
      if (!bindOnce(button)) return;
      var panel = document.getElementById(button.getAttribute('aria-controls'));
      button.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });

    // The copy button needs the clipboard; without it the code can still be selected.
    var canCopy = !!(navigator.clipboard && navigator.clipboard.writeText);
    document.querySelectorAll('[data-hol-copy]').forEach(function (button) {
      button.hidden = !canCopy;
    });

    enhanceSelects(document);
    document.querySelectorAll('.hol-sets__grid').forEach(function (grid) {
      if (bindOnce(grid)) alignGiftRows(grid);
    });

    var detail = document.getElementById('hol-detail');
    if (detail) initDialog(detail);
    initFinder();
  }

  initStickyOffsets();
  initBackToTop();
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
